// Enhanced API cache with TTL and better management
class ApiCache {
  constructor(ttl = 10 * 60 * 1000) { // Increased from 5 to 10 minutes
    this.cache = new Map();
    this.ttl = ttl;
    this.maxSize = 200; // Maximum cache entries to prevent memory issues
  }

  generateKey(url, params = {}) {
    return `${url}_${JSON.stringify(params)}`;
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;

    const now = Date.now();
    if (now > item.expiry) {
      this.cache.delete(key);
      return null;
    }

    // Update last accessed time for LRU
    item.lastAccessed = now;
    return item.data;
  }

  set(key, data) {
    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictOldest();
    }

    const expiry = Date.now() + this.ttl;
    this.cache.set(key, {
      data,
      expiry,
      lastAccessed: Date.now()
    });
  }

  evictOldest() {
    let oldestKey = null;
    let oldestTime = Infinity;

    for (const [key, value] of this.cache.entries()) {
      if (value.lastAccessed < oldestTime) {
        oldestTime = value.lastAccessed;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  clear() {
    this.cache.clear();
  }

  delete(key) {
    this.cache.delete(key);
  }

  // Clean up expired entries
  cleanup() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiry) {
        this.cache.delete(key);
      }
    }
  }

  // Get cache statistics
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      ttl: this.ttl
    };
  }
}

export const apiCache = new ApiCache();

// Periodic cleanup every 5 minutes
setInterval(() => {
  apiCache.cleanup();
}, 5 * 60 * 1000);

export default ApiCache;
